import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken } from 'firebase/messaging';
import ConfigBusiness from '../config/ConfigBusiness.js';

var FCM_REGISTRO_STORAGE_KEY = 'fcm_registro';
var FCM_TOKEN_LEGACY_STORAGE_KEY = 'fcm_token';

function getOrInitApp() {
    var apps = getApps();
    return apps.length > 0 ? apps[0] : initializeApp(ConfigBusiness.get('firebase'));
}

function getApiBase() {
    try {
        var config = JSON.parse(localStorage.getItem('config')) || {};
        return config.apiUrlBase || '';
    } catch (e) {
        return '';
    }
}

function leerJson(clave) {
    try {
        return JSON.parse(localStorage.getItem(clave));
    } catch (e) {
        return null;
    }
}

function usuarioIdActual() {
    var persona = leerJson('persona');
    return (persona && persona.usuario && persona.usuario.id) || null;
}

function registroGuardado() {
    return leerJson(FCM_REGISTRO_STORAGE_KEY) || {};
}

function estaRegistradoParaUsuarioActual(fcmToken) {
    var registro = registroGuardado();
    return registro.token === fcmToken && registro.usuarioId === usuarioIdActual();
}

function guardarRegistro(fcmToken) {
    localStorage.setItem(
        FCM_REGISTRO_STORAGE_KEY,
        JSON.stringify({ token: fcmToken, usuarioId: usuarioIdActual() })
    );
}

function postToken(fcmToken) {
    var authToken = localStorage.getItem('token') || '';
    if (!authToken) {
        return Promise.resolve();
    }
    return fetch(getApiBase() + '/notificaciones/fcm-token', {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization-Token': authToken,
        },
        body: JSON.stringify({ token: fcmToken, dispositivo: 'web' }),
    }).then(function(response) {
        if (!response.ok) {
            throw new Error('El backend respondió ' + response.status);
        }
        guardarRegistro(fcmToken);
    }).catch(function(err) {
        console.error('[FcmTokenService] Error al registrar token:', err);
    });
}

function olvidarRegistro() {
    localStorage.removeItem(FCM_REGISTRO_STORAGE_KEY);
    localStorage.removeItem(FCM_TOKEN_LEGACY_STORAGE_KEY);
}

function deleteToken(fcmToken) {
    // Limpiar localStorage siempre, independientemente de si el backend responde bien.
    // Si no se limpia aquí, el próximo login ve el registro viejo y no re-registra.
    olvidarRegistro();

    var authToken = localStorage.getItem('token') || '';
    if (!fcmToken || !authToken) {
        return Promise.resolve();
    }
    return fetch(getApiBase() + '/notificaciones/fcm-token', {
        method: 'DELETE',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization-Token': authToken,
        },
        body: JSON.stringify({ token: fcmToken }),
        keepalive: true,
    }).catch(function(err) {
        console.error('[FcmTokenService] Error al eliminar token:', err);
    });
}

var FcmTokenService = {
    /**
     * Requests push permission (if not already granted/denied) and registers
     * the FCM token with the backend. Safe to call on every app load — it
     * short-circuits if permission was already denied or if this browser's token
     * is already registered for the current user.
     * Returns a promise that resolves to true if the token was registered.
     */
    solicitarPermisoPush: function() {
        if (!('Notification' in window) || !('serviceWorker' in navigator)) {
            return Promise.resolve(false);
        }

        if (Notification.permission === 'denied') {
            return Promise.resolve(false);
        }

        return Notification.requestPermission().then(function(permission) {
            if (permission !== 'granted') {
                return false;
            }

            var app = getOrInitApp();
            var messaging = getMessaging(app);

            // Desregistrar el SW estático anterior si todavía está activo.
            // Necesario para la transición de /firebase-messaging-sw.js al endpoint dinámico.
            var desregistrarViejo = navigator.serviceWorker.getRegistrations().then(function(registrations) {
                registrations.forEach(function(reg) {
                    if (reg.active && reg.active.scriptURL.indexOf('firebase-messaging-sw.js') !== -1) {
                        reg.unregister();
                    }
                });
            });

            return desregistrarViejo
                .then(function() {
                    return navigator.serviceWorker.register(getApiBase() + '/public/push/firebase-sw.js');
                })
                .then(function(registration) {
                    return getToken(messaging, {
                        vapidKey: ConfigBusiness.get('firebase.vapidKey'),
                        serviceWorkerRegistration: registration,
                    });
                })
                .then(function(currentToken) {
                    if (!currentToken) {
                        return false;
                    }
                    // El token identifica al navegador, no a la persona: solo se omite el
                    // registro si ya quedó asociado al usuario que está logueado ahora.
                    if (estaRegistradoParaUsuarioActual(currentToken)) {
                        return true;
                    }
                    // Token nuevo o rotado — registrar en el backend.
                    return postToken(currentToken).then(function() { return true; });
                });
        }).catch(function(err) {
            console.error('[FcmTokenService] Error al solicitar permiso push:', err);
            return false;
        });
    },

    /**
     * Registers the token only if the user already granted push permission, so it
     * never shows the browser prompt and can run without a user gesture (e.g. when
     * the session starts). Asking for permission stays tied to an explicit action.
     */
    registrarSiYaHayPermiso: function() {
        if (!('Notification' in window) || Notification.permission !== 'granted') {
            return Promise.resolve(false);
        }
        return FcmTokenService.solicitarPermisoPush();
    },

    /**
     * Deletes the current device's FCM token from the backend.
     * Call this before clearing localStorage on logout.
     */
    eliminarTokenActual: function() {
        return deleteToken(registroGuardado().token);
    },

    /**
     * Forgets locally which user this browser's token was registered for, without
     * calling the backend. For when the session is no longer valid (expired or
     * revoked): the DELETE would be rejected, and keeping the record would make
     * the next user of this browser look already registered.
     */
    olvidarRegistro: olvidarRegistro,
};

export default FcmTokenService;
